import React, { useState } from 'react';
import { X, Mail, Shield, Loader2, UserPlus } from 'lucide-react';
import { userApiClient } from '../lib/api/client';
import { logger } from '../lib/utils/logger';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
  onSuccess: () => void;
}

/**
 * InviteMemberModal - Modal for inviting new members to an organization
 */
export const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  organizationName,
  onSuccess,
}) => {
  const DEFAULT_ROLE = 'user';
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(DEFAULT_ROLE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  // Whether the invitation email actually went out. The invite row is always
  // created (record-first); a false here means the provider send failed, so we
  // avoid claiming "sent" and tell the admin the invite can be resent.
  const [emailSent, setEmailSent] = useState(true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Trailing slash is required: the backend route is POST /invitations/ and
      // FastAPI 307-redirects the slashless URL. That redirect is transparent
      // same-origin (dev Vite proxy) but breaks the cross-origin CORS preflight
      // on staging/prod (app.* → api.*), surfacing as "Network request failed".
      const result = await userApiClient.post<{ emailSent?: boolean }>(
        `/api/v1/organizations/${organizationId}/invitations/`,
        {
          email: email.trim(),
          role,
        },
      );

      // Default to true for backward compatibility if the field is absent.
      const delivered = result.emailSent ?? true;
      setEmailSent(delivered);

      logger.info(
        `Invited ${email} to ${organizationName} as ${role} (email ${delivered ? 'sent' : 'not delivered'})`,
      );
      setSuccess(true);

      // Show the result message a bit longer when delivery failed so the admin
      // has time to read that the email did not go out.
      setTimeout(
        () => {
          setSuccess(false);
          setEmailSent(true);
          setEmail('');
          setRole(DEFAULT_ROLE);
          onSuccess();
          onClose();
        },
        delivered ? 1500 : 4000,
      );
    } catch (err: unknown) {
      logger.error('Failed to invite member:', err);
      if (err && typeof err === 'object' && 'message' in err) {
        setError(err.message as string);
      } else {
        setError('Failed to send invitation. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setRole('user');
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-zinc-800 rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-sky-100 dark:bg-sky-900/30 rounded-lg flex items-center justify-center">
              <UserPlus className="h-5 w-5 text-sky-600 dark:text-sky-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Invite Member</h2>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">to {organizationName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="h-5 w-5 text-zinc-500 dark:text-zinc-400" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {success && emailSent && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-700 dark:text-green-400">
                Invitation sent successfully!
              </p>
            </div>
          )}

          {success && !emailSent && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Invitation created, but the email could not be delivered. Check the email provider
                configuration, or resend the invitation once it is fixed.
              </p>
            </div>
          )}

          {/* Email Input */}
          <div>
            <label
              htmlFor="invite-email"
              className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2"
            >
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email Address
              </div>
            </label>
            <input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading || success}
              placeholder="colleague@example.com"
              className="w-full px-4 py-2 border border-zinc-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              autoFocus
            />
            <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
              An invitation email will be sent to this address
            </p>
          </div>

          {/* Role Selection */}
          <div>
            <label
              htmlFor="invite-role"
              className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2"
            >
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Role
              </div>
            </label>
            <select
              id="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={loading || success}
              className="w-full px-4 py-2 border border-zinc-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-sky-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="viewer">Viewer - Read-only access</option>
              <option value="user">User - Standard member access</option>
              <option value="admin">Admin - Full management access</option>
            </select>
            <div className="mt-3 space-y-2 text-xs text-zinc-600 dark:text-zinc-400">
              <p>
                <strong>Viewer:</strong> Can view but not modify resources
              </p>
              <p>
                <strong>User:</strong> Can create and manage their own resources
              </p>
              <p>
                <strong>Admin:</strong> Can manage all resources and members
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading || success}
              className="flex-1 px-4 py-2 border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 font-medium rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || success || !email.trim()}
              className="flex-1 px-4 py-2 bg-sky-600 hover:bg-sky-700 disabled:bg-zinc-400 dark:disabled:bg-zinc-600 text-white font-medium rounded-lg transition-colors disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {(() => {
                if (loading) {
                  return (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  );
                }
                if (success) {
                  return (
                    <>
                      <Mail className="h-4 w-4" />
                      {emailSent ? 'Sent!' : 'Created'}
                    </>
                  );
                }
                return (
                  <>
                    <Mail className="h-4 w-4" />
                    Send Invitation
                  </>
                );
              })()}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
